import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Empty, Image, Modal, Popconfirm, Space, Spin, Table, message, Form } from 'antd';
import Title from 'antd/lib/typography/Title';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import BookyBox from '../../Components/Layout/BookyBox';
import NotFound from '../../Components/Page/NotFoundPage';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { TextService } from '../../Services/TextService';
import { UiControlType } from '../../Types/UiControlType';
import CustomListItemForm from '../CustomList/CustomListItemForm';
import dayjs from 'dayjs';
import { WebApiCompanyDtoCompanyPublicSiteDto, WebApiCustomListsDtoCreateCustomListItemDto, WebApiCustomListsDtoCustomListItemDto, WebApiCustomListsDtoUpdateCustomListItemDto, WebApiModelsCustomListField, WebApiPublicSiteDtoEditableCustomListDto } from '../../Repository/eventbookyapi';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';

const PublicSiteCustomList = () => {
    const params = useParams();
    const navigate = useNavigate();
    const GetText = TextService.GetText;

    const [loading, setLoading] = useState(true);
    const [companyNotFound, setCompanyNotFound] = useState(false);
    const [publicSite, setPublicSite] = useState<WebApiCompanyDtoCompanyPublicSiteDto | null>(null);
    const [editableLists, setEditableLists] = useState<WebApiPublicSiteDtoEditableCustomListDto[]>([]);
    const [customFields, setCustomFields] = useState<WebApiModelsCustomListField[]>([]);
    const [items, setItems] = useState<WebApiCustomListsDtoCustomListItemDto[]>([]);
    const [lookupOptionsByField, setLookupOptionsByField] = useState<Record<number, WebApiCustomListsDtoCustomListItemDto[]>>({});

    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [form] = Form.useForm();

    const customListId = Number(params.customListId);

    const selectedList = useMemo(() => {
        return editableLists.find(l => l.customListId === customListId);
    }, [editableLists, customListId]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    const loadLookupOptions = async (fields: WebApiModelsCustomListField[]) => {
        const lookupFields = fields.filter(f => f.uiControl === UiControlType.Lookup && f.lookupListId && f.id != null);
        if (lookupFields.length === 0) {
            setLookupOptionsByField({});
            return;
        }

        const entries = await Promise.all(lookupFields.map(async field => {
            try {
                const lookupItems = await PublicSiteService.GetEditableCustomListItems(field.lookupListId!);
                return [Number(field.id), lookupItems] as [number, WebApiCustomListsDtoCustomListItemDto[]];
            } catch {
                return [Number(field.id), []] as [number, WebApiCustomListsDtoCustomListItemDto[]];
            }
        }));

        setLookupOptionsByField(Object.fromEntries(entries));
    };

    const loadListData = async (listId: number) => {
        const [fields, listItems] = await Promise.all([
            PublicSiteService.GetEditableCustomListFields(listId),
            PublicSiteService.GetEditableCustomListItems(listId)
        ]);
        setCustomFields(fields || []);
        setItems(listItems || []);
        await loadLookupOptions(fields || []);
    };

    useEffect(() => {
        const init = async () => {
            if (!params.companyurl) {
                setCompanyNotFound(true);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const [company, lists] = await Promise.all([
                    PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl),
                    PublicSiteService.GetEditableCustomLists()
                ]);

                setPublicSite(company);
                setEditableLists(lists || []);

                if (!lists || lists.length === 0) {
                    setCustomFields([]);
                    setItems([]);
                    return;
                }

                const validListId = Number(params.customListId);
                const existingList = lists.find(l => l.customListId === validListId);
                const targetListId = existingList?.customListId || lists[0].customListId;

                if (!existingList && targetListId) {
                    navigate(`/${getCurrentLanguage()}/${params.companyurl}/public-customlist/${targetListId}`, { replace: true });
                    return;
                }

                if (targetListId) {
                    await loadListData(targetListId);
                }
            } catch (error) {
                console.error('Error loading editable custom list:', error);
                setCompanyNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.companyurl, params.customListId]);

    const getFieldValue = (item: WebApiCustomListsDtoCustomListItemDto, fieldId?: number) => {
        return item.fieldValues?.find(f => f.fieldId === fieldId)?.fieldValue;
    };

    const mapInitialValuesForEdit = (item: WebApiCustomListsDtoCustomListItemDto) => {
        const initialValues: any = {};
        customFields.forEach(field => {
            let value = getFieldValue(item, field.id);

            if (field.uiControl === UiControlType.Date && value) {
                value = dayjs(value);
            }

            if (field.uiControl === UiControlType.Number || field.uiControl === UiControlType.Currency || field.uiControl === UiControlType.Decimal) {
                value = value != null ? Number(value) : null;
            }

            if (field.uiControl === UiControlType.Lookup && value != null) {
                value = String(value);
            }

            initialValues[String(field.id)] = value;
        });
        return initialValues;
    };

    const openAdd = () => {
        const initialValues: any = {};
        customFields.forEach(field => {
            if (field.uiControl === UiControlType.YesNo) {
                initialValues[String(field.id)] = false;
            } else if (field.uiControl === UiControlType.Number || field.uiControl === UiControlType.Currency || field.uiControl === UiControlType.Decimal) {
                initialValues[String(field.id)] = null;
            } else {
                initialValues[String(field.id)] = undefined;
            }
        });
        form.setFieldsValue(initialValues);
        setAddOpen(true);
    };

    const openEdit = (item: WebApiCustomListsDtoCustomListItemDto) => {
        form.setFieldsValue(mapInitialValuesForEdit(item));
        setSelectedItem(item);
        setEditOpen(true);
    };

    const handleAdd = async () => {
        try {
            const values = await form.validateFields();
            const dto: WebApiCustomListsDtoCreateCustomListItemDto = {
                customListId,
                fieldValues: Object.entries(values).map(([fieldId, fieldValue]) => ({
                    fieldId: Number(fieldId),
                    fieldValue: fieldValue ?? ""
                }))
            };
            await PublicSiteService.CreateEditableCustomListItem(customListId, dto);
            message.success(GetText('CustomList_ItemAdded'));
            setAddOpen(false);
            await loadListData(customListId);
        } catch {
            message.error(GetText('CustomList_FailedToAdd'));
        }
    };

    const handleEdit = async () => {
        if (!selectedItem?.id) return;
        try {
            const values = await form.validateFields();
            const dto: WebApiCustomListsDtoUpdateCustomListItemDto = {
                id: selectedItem.id,
                fieldValues: Object.entries(values)
                    .filter(([_, value]) => value !== undefined && value !== null && value !== "")
                    .map(([fieldId, fieldValue]) => ({
                        fieldId: Number(fieldId),
                        fieldValue
                    }))
            };

            await PublicSiteService.UpdateEditableCustomListItem(customListId, selectedItem.id, dto);
            message.success(GetText('CustomList_ItemUpdated'));
            setEditOpen(false);
            setSelectedItem(null);
            await loadListData(customListId);
        } catch {
            message.error(GetText('CustomList_FailedToUpdate'));
        }
    };

    const handleDelete = async (id?: number) => {
        if (!id) return;
        try {
            await PublicSiteService.DeleteEditableCustomListItem(customListId, id);
            message.success(GetText('CustomList_ItemDeleted'));
            await loadListData(customListId);
        } catch {
            message.error(GetText('CustomList_FailedToDelete'));
        }
    };

    if (companyNotFound) {
        return <NotFound />;
    }

    if (loading) {
        return (
            <PagePublicSite companyUrl={params.companyurl!}>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </PagePublicSite>
        );
    }

    return (
        <PagePublicSite companyUrl={params.companyurl!}>
            {publicSite && (
                <>
                    {publicSite.siteLogoPath ? (
                        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                            <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                        </div>
                    )}
                    <PublicSiteMenu />
                </>
            )}

            <BookyBox>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h2 style={{ margin: 0 }}>{selectedList?.name || GetText('CustomList_List')}</h2>
                    <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>
                        {GetText('CustomList_AddItem')}
                    </Button>
                </div>

                <Table
                    rowKey={(record) => record.id!}
                    dataSource={items}
                    locale={{ emptyText: <Empty description={GetText('MemberList_EmptyMessage')} /> }}
                    scroll={{ x: true }}
                    columns={[
                        ...customFields.map(field => ({
                            title: field.nameAdmin,
                            key: String(field.id),
                            render: (_: any, record: WebApiCustomListsDtoCustomListItemDto) => {
                                const value = getFieldValue(record, field.id);
                                if (value == null || value === '') return '-';
                                if (field.uiControl === UiControlType.YesNo) return value === true ? GetText('Common_Yes') : GetText('Common_No');
                                if (field.uiControl === UiControlType.Date) {
                                    const dateValue = dayjs(value);
                                    return dateValue.isValid() ? dateValue.format('YYYY-MM-DD') : String(value);
                                }
                                return String(value);
                            }
                        })),
                        {
                            title: GetText('MemberList_ActionColumn'),
                            key: 'actions',
                            render: (_: any, record: WebApiCustomListsDtoCustomListItemDto) => (
                                <Space>
                                    <Button size='small' icon={<EditOutlined />} onClick={() => openEdit(record)}>
                                        {GetText('Common_Edit')}
                                    </Button>
                                    <Popconfirm
                                        title={GetText('Common_ConfirmDelete')}
                                        onConfirm={() => handleDelete(record.id)}
                                        okText={GetText('Common_Yes')}
                                        cancelText={GetText('Common_No')}
                                    >
                                        <Button size='small' danger icon={<DeleteOutlined />}>
                                            {GetText('Common_Delete')}
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            )
                        }
                    ]}
                />
            </BookyBox>

            <Modal
                title={GetText('CustomList_AddItem')}
                open={addOpen}
                onOk={handleAdd}
                onCancel={() => setAddOpen(false)}
                okText={GetText('Common_Save')}
                cancelText={GetText('Common_Cancel')}
                destroyOnClose
            >
                <CustomListItemForm
                    form={form}
                    customFields={customFields}
                    mode='add'
                    lookupOptionsByField={lookupOptionsByField}
                />
            </Modal>

            <Modal
                title={GetText('CustomList_EditItem')}
                open={editOpen}
                onOk={handleEdit}
                onCancel={() => {
                    setEditOpen(false);
                    setSelectedItem(null);
                }}
                okText={GetText('Common_Save')}
                cancelText={GetText('Common_Cancel')}
                destroyOnClose
            >
                <CustomListItemForm
                    form={form}
                    customFields={customFields}
                    mode='edit'
                    lookupOptionsByField={lookupOptionsByField}
                />
            </Modal>
        </PagePublicSite>
    );
};

export default PublicSiteCustomList;
